import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { showError } from '@/app/actions';
import { useAppAuth } from '@/modules/auth/hooks/useAppAuth';
import { getAuthMode } from '@/modules/auth/mode';
import { ICity } from '@/modules/common/model/city';
import { loadCities } from '@/modules/common/store/actions';
import { loadDriver } from '@/modules/driver/actions/async/load-driver';
import { getMyDriver } from '@/modules/driver/store/selectors';
import { loadPartner } from '@/modules/partner/actions/async/loadPartner';
import { setEmptyPartner } from '@/modules/partner/actions/async/setEmptyPartner';
import { IPartner } from '@/modules/partner/model';
import {
  getPartner,
  getTestDefinitions,
  isTestDefinitionsReady,
} from '@/modules/partner/store/selectors';
import { loadUser } from '@/modules/user/actions';
import { User } from '@/modules/user/model';
import { selectIsChauffeur } from '@/modules/user/store/selectors';
import { AppDispatch, RootState } from '@/root-store';
import { eventTracking } from '@/services/event-tracking';
import { createFlowJournal, JournalData } from '@/services/flowJournal';
import { IHistory } from '@/services/HistoryExtended';

import { getUrlForTest } from '../Todo/hooks/getUrlForTest';

const FLOW = 'typeform_redirect';

// Per tab: a mount after the first starts on the data the previous one left behind.
let mountCount = 0;

const describeError = (error: unknown) => (error instanceof Error ? error.message : String(error));

const citiesWithTest = (cities: ICity[], testId?: string) =>
  cities
    .filter(city => city.testDefinitions.some(test => test.id === testId))
    .map(city => city.id)
    .join(',');

const TypeFormRedirect = () => {
  const intl = useIntl();
  const dispatch = useDispatch() as AppDispatch;
  const store = useStore<RootState>();
  const { isAuthenticated } = useAppAuth();

  const isChauffeur = useSelector(selectIsChauffeur);

  const driver = useSelector(getMyDriver);
  const partner = useSelector(getPartner);

  const [isLoadingUser, setIsLoadingUser] = useState(true);
  // Set by this mount only: the store may already hold a partner and cities
  // while the driver is still on its way, and judging then turns the test away.
  const [isDriverLoaded, setIsDriverLoaded] = useState(false);

  const { testId } = useParams<{ testId: string | undefined }>();

  const journal = useMemo(() => createFlowJournal(FLOW), []);
  const mountRef = useRef(0);

  useEffect(() => {
    mountCount += 1;
    mountRef.current = mountCount;
    journal.step('mounted', {
      test_id: testId,
      host: window.location.host,
      auth_mode: getAuthMode(),
      authenticated: isAuthenticated,
      mount: mountCount,
      since_page_load_ms: Math.round(performance.now()),
    });
    // Recorded once per mount, with the values it started with.
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  useEffect(() => {
    journal.step('auth_state', { authenticated: isAuthenticated });
  }, [journal, isAuthenticated]);

  const track = async <T,>(
    name: string,
    run: () => Promise<T>,
    describe: (result: T) => JournalData,
  ): Promise<T> => {
    journal.step(`${name}_started`);

    try {
      const result = await run();

      journal.step(`${name}_loaded`, describe(result));

      return result;
    } catch (error) {
      journal.report(`${name}_failed`, { test_id: testId, error: describeError(error) });

      throw error;
    }
  };

  useEffect(() => {
    const loadUserAndCityData = async () => {
      await Promise.all([
        track(
          'user',
          () => dispatch(loadUser()).unwrap(),
          (user: User) => ({
            user_id: user.userId,
            roles: user.roles.join(','),
            city_id: user.cityId,
          }),
        ),
        track(
          'cities',
          () => dispatch(loadCities()).unwrap(),
          (cities: ICity[]) => ({
            cities: cities.length,
            test_city_ids: citiesWithTest(cities, testId),
          }),
        ),
      ]);

      setIsLoadingUser(false);
    };

    if (isAuthenticated) {
      loadUserAndCityData();
    }
    // track is rebuilt every render; the journal it writes to lives as long as the mount.
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    const loadPartnerAndDriverData = async () => {
      setIsDriverLoaded(false);

      const describePartner = (loaded: IPartner) => ({
        partner_id: loaded.id,
        city_id: loaded.cityId,
      });

      if (isChauffeur) {
        await track('empty_partner', () => dispatch(setEmptyPartner()).unwrap(), describePartner);
      } else {
        await track('partner', () => dispatch(loadPartner()).unwrap(), describePartner);
      }

      await track(
        'driver',
        () => dispatch(loadDriver()).unwrap(),
        loaded => ({ driver_id: loaded?.id, city_id: loaded?.cityId }),
      );

      setIsDriverLoaded(true);
    };

    if (!isLoadingUser) {
      loadPartnerAndDriverData();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [dispatch, isChauffeur, isLoadingUser]);

  const isTestsReady = useSelector(isTestDefinitionsReady);
  const tests = useSelector(getTestDefinitions);
  const test = tests.find(current => current.id === testId);
  const history = useHistory() as IHistory;

  useEffect(() => {
    if (!isAuthenticated) {
      journal.step('sent_to_login');
      history.replaceWithRollback('/login');
    }
  }, [isAuthenticated, history, journal]);

  useEffect(() => {
    if (!isDriverLoaded || !isTestsReady || !partner.cityName) {
      return;
    }

    const trackOutcome = (result: string) =>
      eventTracking.trackSnowplow({
        event: 'Onboarding - Test - Redirect',
        id: testId,
        result,
        host: window.location.host,
      });

    // test not found
    if (!test || !driver.id) {
      const state = store.getState();
      const reason = test ? 'driver_missing' : 'test_not_found';

      trackOutcome(reason);
      journal.report(reason, {
        test_id: testId,
        mount: mountRef.current,
        host: window.location.host,
        auth_mode: getAuthMode(),
        user_id: state.user.data.userId,
        roles: state.user.data.roles.join(','),
        is_chauffeur: isChauffeur,
        partner_id: partner.id,
        partner_city_id: partner.cityId,
        driver_id: driver.id,
        driver_city_id: driver.cityId,
        cities: state.common.cities.length,
        tests_in_city: tests.length,
        test_city_ids: citiesWithTest(state.common.cities, testId),
      });

      // hard reload
      dispatch(
        showError(
          intl.formatMessage({
            defaultMessage: 'You are not allowed to perform this action.',
            id: 'VMylfX',
            description: 'TypeFormRedirect: Test not allowed',
          }),
        ),
      );
      history.push('/');

      return;
    }

    trackOutcome('typeform');
    journal.step('redirect', { test_id: testId, link_type: test.linkType });

    window.location.href = getUrlForTest(test, {
      driverId: driver.id,
      cityName: partner.cityName,
    });
    // The report reads a snapshot; listing its fields here would re-run the decision.
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [intl, driver.id, partner.cityName, isTestsReady, test, dispatch, history, isDriverLoaded]);

  return <></>;
};

export { TypeFormRedirect };
